import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Input,
  Button,
  Space,
  Form,
  Checkbox,
  Row,
  Col,
  Modal,
} from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useAutoRequest } from '../../hooks/useAutoRequest';
import { parseIds } from '../../utils/parseIds';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

/**
 * Página para criar um novo Auto Request (CW2).
 * 
 * Permite que o usuário insira uma lista de CNJs para monitoramento.
 * Cada CNJ deve estar em uma linha separada.
 */
const AutoRequestContent: React.FC = () => {
  const { setCnjs } = useAutoRequest();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const cnjsRef = useRef<HTMLTextAreaElement>(null);
  const [showExamplesModal, setShowExamplesModal] = useState(false);

  /**
   * Handler para submeter o formulário.
   */
  const handleSubmit = async (values: { cnjs: string; makeDownload?: boolean; forced?: boolean }) => {
    const cnjsText = values.cnjs || '';
    if (cnjsText.trim()) {
      const cnjList = parseIds(cnjsText);

      if (cnjList.length > 0) {
        setCnjs(cnjList, {
          makeDownload: values.makeDownload || false,
          forced: values.forced || false,
        });
        navigate('/auto-request/monitoring');
      } else {
        setCnjs([]);
      }
    }
  };

  /**
   * Exemplos de CNJs para referência.
   */
  const exampleCnjs = [
    '5003060-23.2015.4.04.7209',
    '5010205-96.2016.4.04.7209',
    '5013857-07.2023.8.13.0027',
    '5000042-64.2019.4.04.7205',
  ];

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Title level={2} style={{ marginBottom: 0 }}>
              <FileTextOutlined /> Novo Auto Request
            </Title>
            <Button type="primary" onClick={() => setShowExamplesModal(true)}>
              Ver exemplos
            </Button>
          </div>
          <Paragraph>
            Digite os CNJs que deseja monitorar o status e o retorno.
            Você pode usar dois formatos: um CNJ por linha ou um array JSON.
          </Paragraph>
        </div>

        <Modal
          title="Formatos Aceitos"
          open={showExamplesModal}
          onCancel={() => setShowExamplesModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowExamplesModal(false)}>
              Fechar
            </Button>,
          ]}
          width={1200}
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text strong>Formato 1 - Múltiplas linhas:</Text>
                <TextArea
                  value={exampleCnjs.join("\n")}
                  readOnly
                  rows={exampleCnjs.length}
                  style={{ fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                />
              </Space>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <Text strong>Formato 2 - Array JSON:</Text>
                <TextArea
                  value={JSON.stringify(exampleCnjs, null, 2)}
                  readOnly
                  rows={JSON.stringify(exampleCnjs, null, 2).split("\n").length}
                  style={{ fontFamily: "monospace", resize: "none", overflow: "hidden" }}
                />
              </Space>
            </Col>
          </Row>
        </Modal>

        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="cnjs"
            label="Lista de CNJs"
            rules={[
              { required: true, message: 'Por favor, insira pelo menos um CNJ!' },
              {
                validator: (_, value) => {
                  if (!value || value.trim().length === 0) {
                    return Promise.reject(new Error("Por favor, insira pelo menos um CNJ"));
                  }
                  const cnjList = parseIds(value);
                  if (cnjList.length === 0) {
                    return Promise.reject(new Error("Por favor, insira pelo menos um CNJ válido"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <TextArea
              ref={cnjsRef}
              rows={10}
              placeholder='Digite os CNJs (um por linha) ou um array JSON: ["cnj1", "cnj2", ...]'
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item
            name="makeDownload"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Make Download</Checkbox>
          </Form.Item>

          <Form.Item
            name="forced"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>Forced</Checkbox>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" size="large" block>
              Monitorar
            </Button>
          </Form.Item>
          </Form>
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página AutoRequest.
 */
const AutoRequest: React.FC = () => {
  return (
    <AutoRequestContent />
  );
};

export default AutoRequest;
